/**
 * Block-sensitive query invalidation (#3258, PR #3266).
 *
 * The backend block-filters a set of read surfaces at the SQL level, but the
 * filter only applies on the NEXT fetch — already-cached queries stay fresh
 * for their staleTime (30s–5min), so blocking keeps showing (and unblocking
 * keeps hiding) the other user's content until each cache expires. Both
 * mutation paths (block on PublicProfilePage, unblock on BlockedUsersPage)
 * call this ONE helper so the prefix list cannot drift between them.
 *
 * Layer note: `lib/` must not import from `hooks/` (ESLint layer boundary,
 * #1876), so the key prefixes are hardcoded here. Each prefix maps to the
 * backend surface that filters it:
 *
 * | Query key prefix        | Web hook                                   | Block-filtered backend surface                              |
 * |-------------------------|--------------------------------------------|-------------------------------------------------------------|
 * | ['room-messages']       | useRoomMessages                            | GET /rooms/:roomId/messages (messages, pinned announcement, |
 * |                         |                                            | pinned message, reaction aggregates)                        |
 * | ['reaction-users']      | useReactionUsers                           | GET /rooms/:roomId/messages/:messageId/reactions/:emoji     |
 * | ['clubMembers']         | useClubMembers / useAllClubMembers         | GET /clubs/:slug/members (roster + count)                   |
 * | ['club']                | useClubBySlug (['club', slug]) /           | GET /clubs/:slug and GET /clubs/id/:id — stats.member_count |
 * |                         | useClubById (['club', 'id', id])           | is block-filtered for signed-in viewers (r4). The prefix    |
 * |                         |                                            | also sweeps nested ['club', id, ...] keys; a slug/id is     |
 * |                         |                                            | unknown at block time, so the whole prefix goes (matches    |
 * |                         |                                            | useMembershipRequests' existing ['club'] invalidate).       |
 * | ['membership-requests'] | usePendingMembershipRequests               | GET /clubs/:clubId/membership-requests                      |
 * | ['event-rsvps']         | useEventRSVPs                              | GET /events/:eventId/rsvps (roster + counts)                |
 * | ['event-checkins']      | useEventCheckins                           | GET /events/:id/checkins                                    |
 * | ['training-group']      | useTrainingGroup / useTrainingGroupMembers | GET /training-groups/:id (leader identity) and              |
 * |                         |                                            | GET /training-groups/:id/members                            |
 * | ['training-groups']     | useMyTrainingGroups / useDiscoverable-     | GET /training-groups?member=true / ?discoverable=true /     |
 * |                         | TrainingGroups / useClubTrainingGroups     | ?club_id=… — leader_id + member_count are block-filtered    |
 * |                         |                                            | on every list branch (r5)                                   |
 *
 * Filtered surfaces with NO web cache entry (deliberately absent here):
 * - Push notification fan-out (backend-only, nothing cached client-side).
 * - GET /events/:eventId/rsvp-summary is NOT block-filtered (aggregate counts
 *   carry no identities), so ['event', id, 'rsvp-summary'] needs no entry.
 */
import type { QueryClient } from '@tanstack/react-query';

const BLOCK_SENSITIVE_QUERY_PREFIXES: readonly (readonly string[])[] = [
  ['room-messages'],
  ['reaction-users'],
  ['clubMembers'],
  ['club'],
  ['membership-requests'],
  ['event-rsvps'],
  ['event-checkins'],
  ['training-group'],
  ['training-groups'],
];

/**
 * Invalidate every cached query whose data the backend block-filters, so the
 * next mount refetches under the new block state. Call from BOTH the block
 * and unblock mutation onSuccess handlers (alongside their own
 * ['blocked-users'] invalidate / ['publicProfile', id] remove).
 */
export function invalidateBlockSensitiveQueries(queryClient: QueryClient): void {
  for (const queryKey of BLOCK_SENSITIVE_QUERY_PREFIXES) {
    queryClient.invalidateQueries({ queryKey });
  }
}
